import { api } from './api';
import type {
  FeedbackReview,
  FeedbackSubmissionCreateBody,
  FeedbackReviewListParams,
  FeedbackReviewVerdict,
  FeedbackSubmission,
  FeedbackSubmissionListParams,
  Paged,
  FeedbackDashboard,
  FeedbackDashboardParams,
} from '../types/feedback-submission.types';

/**
 * Aligned to app/api/v1/routers/feedback_submission_router.py and
 * feedback_review_router.py.
 *
 * Two shapes worth noting, because they differ from every other master here:
 *  · a submission has NO update call — it is write-once; close and reopen are the
 *    only writes after insert, and neither touches what the user said;
 *  · a review has NO create call — the server writes one PENDING row per REVIEWER
 *    when feedback arrives, so an SME only ever completes a row that exists.
 */
const SUBMISSIONS = '/api/v1/feedback-submissions';
const REVIEWS = '/api/v1/feedback-reviews';

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
  pagination?: { totalItems?: number; total?: number };
}

function unwrap<T>(res: { data: Envelope<T[] | { items?: T[] }> }): Paged<T> {
  const raw = res.data?.data as unknown;
  const items = Array.isArray(raw) ? (raw as T[]) : ((raw as { items?: T[] })?.items ?? []);
  const p = res.data?.pagination;
  return { items, total: p?.totalItems ?? p?.total ?? items.length };
}

export const feedbackSubmissionService = {
  /** The only write a user makes. Duplicates are allowed, so this never dedupes. */
  async create(body: FeedbackSubmissionCreateBody): Promise<FeedbackSubmission> {
    const res = await api.post<Envelope<FeedbackSubmission>>(`${SUBMISSIONS}/create`, body);
    return res.data.data;
  },

  async list(params: FeedbackSubmissionListParams = {}): Promise<Paged<FeedbackSubmission>> {
    return unwrap<FeedbackSubmission>(
      await api.get(`${SUBMISSIONS}/list`, { params }),
    );
  },

  /** KPIs, trend and breakdowns for the dashboard, under the caller's filters. */
  async dashboard(params: FeedbackDashboardParams = {}): Promise<FeedbackDashboard> {
    const res = await api.get<{ data: FeedbackDashboard }>(`${SUBMISSIONS}/dashboard`, { params });
    return res.data.data;
  },

  /** Feedback the caller submitted themselves. */
  async mine(params: {
    page?: number; page_size?: number;
    componentType?: string; status?: string; search?: string;
  } = {}): Promise<Paged<FeedbackSubmission>> {
    return unwrap<FeedbackSubmission>(await api.get(`${SUBMISSIONS}/my`, { params }));
  },

  /**
   * Feedback routed to the caller as an SME. A separate call from list() because
   * it needs only FEEDBACK_SUBMISSION.READ — being named on a definition is
   * itself the grant, so an SME sees their own queue without being able to read
   * everyone else's feedback.
   */
  async assignedToMe(params: { page?: number; page_size?: number; status?: string } = {}): Promise<Paged<FeedbackSubmission>> {
    return unwrap<FeedbackSubmission>(await api.get(`${SUBMISSIONS}/assigned`, { params }));
  },

  /** The by-id read carries its reviews inline, so the detail view needs one call. */
  async get(id: string): Promise<FeedbackSubmission> {
    const res = await api.get<Envelope<FeedbackSubmission>>(`${SUBMISSIONS}/${id}`);
    return res.data.data;
  },

  async close(id: string, reason?: string): Promise<FeedbackSubmission> {
    const res = await api.patch<Envelope<FeedbackSubmission>>(
      `${SUBMISSIONS}/${id}/close`, { reason: reason || undefined });
    return res.data.data;
  },

  /** An admin settling a case without an SME. Needs FEEDBACK_SUBMISSION.MANAGE. */
  async markReviewed(id: string, note?: string): Promise<FeedbackSubmission> {
    const res = await api.patch<Envelope<FeedbackSubmission>>(
      `${SUBMISSIONS}/${id}/mark-reviewed`, { note: note || undefined });
    return res.data.data;
  },

  /** Triage: put it in front of the SMEs.
   *  With no ids it goes to whoever the definition named, roles and primary intact. */
  async forward(id: string, reviewerUserIds?: string[], primaryUserId?: string):
    Promise<FeedbackSubmission> {
    const res = await api.patch<Envelope<FeedbackSubmission>>(
      `${SUBMISSIONS}/${id}/forward`,
      { reviewerUserIds: reviewerUserIds?.length ? reviewerUserIds : undefined,
        primaryUserId: primaryUserId || undefined });
    return res.data.data;
  },

  async reopen(id: string, reason?: string): Promise<FeedbackSubmission> {
    const res = await api.patch<Envelope<FeedbackSubmission>>(
      `${SUBMISSIONS}/${id}/reopen`, { reason: reason || undefined });
    return res.data.data;
  },
};

export const feedbackReviewService = {
  /** My work queue: assigned to me and not finished. One query, no join. */
  async inbox(params: { page?: number; page_size?: number; appId?: string; scope?: 'AI' | 'CORE' } = {}): Promise<Paged<FeedbackReview>> {
    return unwrap<FeedbackReview>(await api.get(`${REVIEWS}/inbox`, { params }));
  },

  async list(params: FeedbackReviewListParams = {}): Promise<Paged<FeedbackReview>> {
    return unwrap<FeedbackReview>(await api.get(`${REVIEWS}/list`, { params }));
  },

  async get(id: string): Promise<FeedbackReview> {
    const res = await api.get<Envelope<FeedbackReview>>(`${REVIEWS}/${id}`);
    return res.data.data;
  },

  /** Mark that I have opened it — moves the submission to IN_REVIEW. */
  async claim(id: string): Promise<FeedbackReview> {
    const res = await api.patch<Envelope<FeedbackReview>>(`${REVIEWS}/${id}/claim`, {});
    return res.data.data;
  },

  async recordVerdict(id: string, body: FeedbackReviewVerdict): Promise<FeedbackReview> {
    const res = await api.put<Envelope<FeedbackReview>>(`${REVIEWS}/${id}`, body);
    return res.data.data;
  },

  async remove(id: string): Promise<void> {
    await api.delete(`${REVIEWS}/${id}`);
  },
};

export default feedbackSubmissionService;
